/**
 * Active Patient — session-scoped state shared between ABHA Creation,
 * M2 FHIR Bundles, and M3 Consent Request. Once a tenant onboards or
 * picks a patient on any one screen, the others auto-fill from it.
 *
 * Persisted in sessionStorage (NOT localStorage) so closing the tab
 * clears the patient context — same posture as PHI in the rest of the
 * portal.
 */

export interface ActivePatient {
  /** UHID / memberId — your tenant-side primary key for the patient. */
  memberId: string;
  abhaAddress: string;
  abhaNumber?: string;
  name: string;
  gender?: 'male' | 'female' | 'other' | 'unknown' | string;
  /** ISO YYYY-MM-DD. */
  birthDate?: string;
  mobile?: string;
  /** Free-form display address line. */
  address?: string;
}

const KEY = 'abdm.activePatient';
type Listener = (p: ActivePatient | null) => void;
const listeners = new Set<Listener>();

export const activePatient = {
  get(): ActivePatient | null {
    try {
      const raw = sessionStorage.getItem(KEY);
      return raw ? (JSON.parse(raw) as ActivePatient) : null;
    } catch {
      return null;
    }
  },
  set(p: ActivePatient | null) {
    if (p) sessionStorage.setItem(KEY, JSON.stringify(p));
    else sessionStorage.removeItem(KEY);
    listeners.forEach((fn) => fn(p));
  },
  clear() { activePatient.set(null); },
  subscribe(fn: Listener): () => void {
    listeners.add(fn);
    return () => { listeners.delete(fn); };
  },
};

import { useEffect, useState } from 'react';

export function useActivePatient(): [ActivePatient | null, (p: ActivePatient | null) => void] {
  const [p, setP] = useState<ActivePatient | null>(() => activePatient.get());
  useEffect(() => activePatient.subscribe(setP), []);
  return [p, activePatient.set];
}
